import dayjs, { type Dayjs } from 'dayjs';
import { motion, s } from '@/theme/tokens';
import type { CloneDateQuickFilter } from '@/features/shared/hooks/useCloneDateWindow';

export const buildToolbarIconButtonSx = (
  toolbarInactiveIconColor: string,
  toolbarControlSize: number,
) =>
  ({
    color: toolbarInactiveIconColor,
    p: '0 !important',
    width: toolbarControlSize,
    height: toolbarControlSize,
    minWidth: toolbarControlSize,
    position: 'relative',
    borderRadius: '50%',
    backgroundColor: 'transparent',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    lineHeight: 0,
    '&:hover, &.Mui-focusVisible': {
      backgroundColor: 'transparent',
    },
    '& .MuiSvgIcon-root': {
      display: 'block',
    },
  }) as const;

export const buildToolbarIconCircleSx = (isHovered: boolean): Record<string, string | number> => ({
  width: 28,
  height: 28,
  minWidth: 28,
  minHeight: 28,
  maxWidth: 28,
  maxHeight: 28,
  flex: '0 0 28px',
  aspectRatio: '1 / 1',
  borderRadius: '50%',
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  transition: `background-color ${motion.durations.fast} ${motion.easing.standard}`,
  backgroundColor: isHovered ? 'action.hover' : 'transparent',
});

export const cloneDatePickerOpenButtonSx = (hasValue: boolean) =>
  ({
    transform: `translateX(${s('none')})`,
    '& .MuiSvgIcon-root': {
      mr: s('none'),
      color: hasValue ? 'text.primary' : 'text.secondary',
    },
  }) as const;

export const cloneDatePickerTextFieldSx = {
  mt: `-${s('xs')}`,
  width: `calc(${s('4xl')} + ${s('xl')} + ${s('sm')})`,
  '& .MuiPickersInputBase-root': {
    height: 32,
    minHeight: 32,
    backgroundColor: 'background.default',
    paddingLeft: `${s('sm')} !important`,
    paddingRight: `${s('xs')} !important`,
  },
  '& .MuiPickersOutlinedInput-root, & .MuiPickersInputBase-sectionsContainer, & .MuiPickersSectionList-root, & .MuiPickersInputBase-input':
    { paddingLeft: `${s('sm')} !important` },
  '& .MuiInputAdornment-root': { marginRight: `${s('xs')} !important` },
} as const;

export const cloneDateQuickRangeTextSx = {
  '& .MuiPickersInputBase-input, & .MuiPickersInputBase-sectionContent, & .MuiPickersInputBase-separator, & .MuiPickersInputBase-sectionBefore, & .MuiPickersInputBase-sectionAfter':
    {
      color: 'text.secondary',
    },
  '& .MuiPickersSectionList-sectionSeparator': {
    color: 'text.secondary',
  },
} as const;

export const resolveCloneDatePreview = (
  dateQuickFilter: CloneDateQuickFilter,
  fromDate: Dayjs | null,
  toDate: Dayjs | null,
): {
  isQuickRangePreview: boolean;
  displayedFromDate: Dayjs | null;
  displayedToDate: Dayjs | null;
} => {
  const quickFilterDays = dateQuickFilter === '14d' ? 14 : dateQuickFilter === '30d' ? 30 : 7;
  const isQuickRangePreview = fromDate === null && toDate === null;
  if (!isQuickRangePreview) {
    return {
      isQuickRangePreview,
      displayedFromDate: fromDate,
      displayedToDate: toDate,
    };
  }
  const quickRangeEnd = dayjs().startOf('day');
  const quickRangeStart = quickRangeEnd.subtract(quickFilterDays - 1, 'day');
  return {
    isQuickRangePreview,
    displayedFromDate: quickRangeStart,
    displayedToDate: quickRangeEnd,
  };
};
